// pages/secure/FormsCabinetPage.js
// The forms cabinet: the brokerage's library of blank forms and disclosures,
// one searchable list (category column + filter). Everyone downloads; the managing broker adds and edits
// through pop-ups. Complements the Forms Portal (which generates filled forms).

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const EMPTY_FORM = { title: '', category: 'General', description: '' };

const FormsCabinetPage = () => {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const isMB = !!user?.isManagingBroker;

    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(null);       // {title, category, description, file}
    const [editing, setEditing] = useState(null);     // {form_id, title, category, description}
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/forms-library')
            .then(setForms)
            .catch(err => setError(err.message || 'Failed to open the forms cabinet.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const download = async (f) => {
        try {
            const { data } = await apiClient.post(`/api/forms-library/${f.form_id}/download`, undefined, { responseType: 'blob' });
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = f.file_name;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) { setError(err.message); }
    };

    const saveNew = async () => {
        if (!adding.file) { setError('Choose a file.'); return; }
        setIsSaving(true);
        setError('');
        try {
            const fd = new FormData();
            fd.append('form', adding.file);
            fd.append('title', adding.title);
            fd.append('category', adding.category);
            fd.append('description', adding.description);
            await apiClient.post('/api/forms-library', fd, true);
            setAdding(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const saveEdit = async () => {
        setIsSaving(true);
        setError('');
        try {
            await apiClient.put(`/api/forms-library/${editing.form_id}`, editing);
            setEditing(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const remove = async (f) => {
        if (!window.confirm(`Remove "${f.title}" from the cabinet?`)) return;
        try { await apiClient.delete(`/api/forms-library/${f.form_id}`); load(); }
        catch (err) { setError(err.message); }
    };

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Forms Cabinet</h1>
                {isMB && (
                    <button onClick={() => setAdding({ ...EMPTY_FORM, file: null })}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Add form</button>
                )}
            </div>
            <p className="text-slate-500 mb-6">Blank forms, disclosures, and templates for the whole office. Generated/filled agreements live in the Forms Portal.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}
            {isLoading && <p>Opening the cabinet...</p>}
            {!isLoading && forms.length === 0 && (
                <p className="text-slate-500 bg-white rounded-lg shadow p-6">
                    The cabinet is empty{isMB ? ' — add the first form.' : '. The managing broker will stock it soon.'}
                </p>
            )}

            {!isLoading && forms.length > 0 && (
                <div className="bg-white rounded-lg shadow p-3">
                    <DataList rows={forms} rowKey="form_id" csvName="forms-cabinet" defaultSort={{ key: 'category', dir: 'asc' }}
                        filters={[{ key: 'category', label: 'Category' }]}
                        columns={[
                            { key: 'category', label: 'Category' },
                            { key: 'title', label: 'Form', value: (f) => [f.title, f.description].filter(Boolean).join(' · '), sortValue: (f) => f.title,
                                render: (f) => <div className="min-w-0"><p className="font-medium text-slate-700">📄 {f.title}</p>{f.description && <p className="text-xs text-slate-500">{f.description}</p>}</div> },
                            { key: 'file_name', label: 'File', render: (f) => <span className="text-xs text-slate-500">{f.file_name}</span> },
                            { key: 'file_size', label: 'Size', type: 'number', value: (f) => f.file_size, render: (f) => <span className="text-xs text-slate-500 whitespace-nowrap">{formatSize(f.file_size)}</span> },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (f) => (
                                <span className="flex gap-3 whitespace-nowrap">
                                    <button onClick={() => download(f)} className="text-blue-600 hover:underline text-sm">Download</button>
                                    {isMB && <>
                                        <button onClick={() => setEditing({ form_id: f.form_id, title: f.title, category: f.category, description: f.description || '' })}
                                                className="text-slate-500 hover:underline text-sm">Edit</button>
                                        <button onClick={() => remove(f)} className="text-red-500 hover:underline text-sm">Remove</button>
                                    </>}
                                </span>) },
                        ]} />
                </div>
            )}

            {/* Add form modal */}
            <Modal open={!!adding} onClose={() => setAdding(null)} title="Add a form to the cabinet"
                   footer={<>
                       <button onClick={() => setAdding(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveNew} disabled={isSaving || !adding?.title?.trim() || !adding?.file}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Uploading...' : 'Add to cabinet'}
                       </button>
                   </>}>
                {adding && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Title</label>
                            <input value={adding.title} onChange={e => setAdding(p => ({ ...p, title: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" placeholder="e.g. Residential Real Property Disclosure Report" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Category</label>
                            <input value={adding.category} onChange={e => setAdding(p => ({ ...p, category: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" list="form-categories" />
                            <datalist id="form-categories">
                                {['Disclosures', 'Listing Agreements', 'Buyer Agreements', 'Leases', 'Onboarding & HR', 'IDFPR', 'General'].map(c => <option key={c} value={c} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Description</label>
                            <input value={adding.description} onChange={e => setAdding(p => ({ ...p, description: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" placeholder="When to use it" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">File (PDF/DOC, max 25MB)</label>
                            <input type="file" onChange={e => setAdding(p => ({ ...p, file: e.target.files[0] }))}
                                   className="mt-1 w-full" />
                        </div>
                    </div>
                )}
            </Modal>

            {/* Edit form modal */}
            <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit form details"
                   footer={<>
                       <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveEdit} disabled={isSaving}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Saving...' : 'Save'}
                       </button>
                   </>}>
                {editing && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Title</label>
                            <input value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Category</label>
                            <input value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Description</label>
                            <input value={editing.description} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FormsCabinetPage;
